import React from "react";
import { Check, Loader2, MapPin, Search } from "lucide-react";
import type { Neighborhood } from "../../../types/neighborhood";
import {
  neighborhoodDisplayLabel,
  neighborhoodKey,
} from "../../lib/neighborhoodDisplay";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

type NeighborhoodSelectorContentProps = {
  city: string;
  browseNeighborhoods: Neighborhood[];
  searchNeighborhoods: Neighborhood[];
  isSearchActive: boolean;
  selected: Set<string>;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onToggle: (neighborhood: Neighborhood) => void;
  onSelectAll: () => void;
  remoteSearchLoading: boolean;
  remoteSearchFailed: boolean;
};

function AreaToggleButton({
  neighborhood,
  selected,
  onToggle,
}: {
  neighborhood: Neighborhood;
  selected: Set<string>;
  onToggle: (neighborhood: Neighborhood) => void;
}) {
  const isSelected = selected.has(neighborhoodKey(neighborhood));
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => onToggle(neighborhood)}
      className={`h-auto w-full justify-start gap-2 rounded px-2 py-1.5 text-left text-sm ${
        isSelected
          ? "bg-accent/10 text-accent hover:bg-accent/15"
          : "text-muted-foreground"
      }`}
    >
      <div
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
          isSelected
            ? "border-accent bg-accent text-accent-foreground"
            : "border-muted-foreground/30"
        }`}
      >
        {isSelected && <Check className="h-3 w-3" />}
      </div>
      <span className="truncate">{neighborhoodDisplayLabel(neighborhood)}</span>
    </Button>
  );
}

export const NeighborhoodSelectorContent: React.FC<
  NeighborhoodSelectorContentProps
> = ({
  city,
  browseNeighborhoods,
  searchNeighborhoods,
  isSearchActive,
  selected,
  searchQuery,
  onSearchChange,
  onToggle,
  onSelectAll,
  remoteSearchLoading,
  remoteSearchFailed,
}) => {
  const listNeighborhoods = isSearchActive
    ? searchNeighborhoods
    : browseNeighborhoods;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-4 flex items-center gap-2">
        <MapPin className="h-5 w-5 text-accent" />
        <h3 className="font-medium text-foreground">
          Select towns and areas you serve near {city}
        </h3>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search towns and areas..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        {!isSearchActive && browseNeighborhoods.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onSelectAll}
          >
            Select All
          </Button>
        )}
      </div>

      <div className="max-h-64 overflow-y-auto rounded border border-border bg-muted/30 p-2">
        {isSearchActive && remoteSearchLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Searching areas…
          </div>
        ) : listNeighborhoods.length > 0 ? (
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
            {listNeighborhoods.map((neighborhood) => (
              <AreaToggleButton
                key={neighborhoodKey(neighborhood)}
                neighborhood={neighborhood}
                selected={selected}
                onToggle={onToggle}
              />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {isSearchActive
              ? remoteSearchFailed
                ? "Search timed out or is unavailable. Clear the field to see suggested areas."
                : "No matching towns or areas. Try a different search."
              : "No suggested areas yet. Use search to find towns you serve."}
          </p>
        )}
      </div>
    </div>
  );
};
